import type { NavigationLinks } from './NavLink.types';

import { ArrowRightLeft, Fuel } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { DEFAULT_LANGUAGE } from '../../constants/i18n';
import { WIDGET_BASE_ROUTE } from '../../constants/navigation';
import { useAppSelector } from '../../hooks/reduxHooks';

const BOTTOM_NAV_ICON_SIZE = 20;

export function useNavLinks(): NavigationLinks {
  const { t } = useTranslation();
  const { language } = useAppSelector((state) => state.settings);
  const isDefaultLanguage = language === DEFAULT_LANGUAGE;
  const localized = (path: string) => (isDefaultLanguage ? path : `/${language}${path}`);

  return {
    primaryLinks: [
      {
        id: 'menu-swap-link',
        title: t('Swap'),
        location: isDefaultLanguage ? WIDGET_BASE_ROUTE : `${language}${WIDGET_BASE_ROUTE}`,
        icon: <ArrowRightLeft size={BOTTOM_NAV_ICON_SIZE} />,
      },
      {
        id: 'menu-refuel-link',
        title: t('Refuel'),
        location: localized('/refuel'),
        icon: <Fuel size={BOTTOM_NAV_ICON_SIZE} />,
      },
    ],
    moreGroups: [
      {
        id: 'nav-more-discover-group',
        title: t('Discover'),
        links: [
          {
            id: 'menu-updates-link',
            title: t("What's new?"),
            location: window.Beamer ? '#' : 'https://updates.rango.exchange',
            onClick: () => window.Beamer?.toggle(),
            badge: {
              className: 'badgeWrapper',
              title: (
                <span>
                  {t('New Update')} (<span className="beamerTrigger" />)
                </span>
              ),
              color: 'cyan',
            },
          },
          {
            id: 'menu-leaderboard-link',
            title: t('Leaderboard'),
            location: localized('/leaderboard'),
          },
          {
            id: 'menu-affiliate-link',
            title: t('Affiliate'),
            location: localized('/affiliate'),
          },
        ],
      },
      {
        id: 'nav-more-help-group',
        title: t('Help & Feedback'),
        links: [
          {
            id: 'menu-support-link',
            title: t('Support'),
            location: 'https://discord.gg/q3EngGyTrZ',
          },
          {
            id: 'menu-rango-explorer-link',
            title: t('Explorer'),
            location: 'https://explorer.rango.exchange',
          },
          {
            id: 'menu-contact-us-link',
            title: t('Contact us'),
            location: 'https://rango.exchange/contact-us',
          },
          {
            id: 'menu-bug-report-link',
            title: t('Bug report'),
            location: 'https://docs.rango.exchange/bug-bounty',
          },
          {
            id: 'menu-documentation-link',
            title: t('Documentation'),
            location: 'https://docs.rango.exchange',
          },
          {
            id: 'menu-feature-request-link',
            title: t('Feature request'),
            location: 'https://updates.rango.exchange/ideas/en',
          },
        ],
      },
    ],
  };
}
